import React from 'react';
import clsx from 'clsx';
import BlurIcon from '@mui/icons-material/BlurOnOutlined';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import NoneIcon from '@mui/icons-material/NotInterestedOutlined';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';

export type Thumbnail = 'none' | 'blur' | 'image';

interface BackgroundThumbnailProps {
  thumbnail: Thumbnail;
  imagePath?: string;
  name?: string;
  index?: number;
}

const useStyles = makeStyles(() =>
  createStyles({
    thumbContainer: {
      margin: '5px',
      width: 'calc(50% - 10px)',
      display: 'flex',
      position: 'relative',
      '&::after': {
        content: '""',
        paddingBottom: '55.5%',
      },
      '&:hover': {
        cursor: 'pointer',
      },
    },
    thumbIconContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '10px',
      border: 'solid var(--sib-color_surface-hover-background)',
      '&.selected': {
        border: 'solid var(--sib-color_primary-default)',
        '& svg': {
          color: 'var(--sib-color_primary-default)',
        },
      },
    },
    thumbIcon: {
      height: 50,
      width: 50,
      color: 'var(--sib-color_surface-hover-background)',
      '&.selected': {
        color: 'var(--sib-color_primary-default)',
      },
    },
    thumbImage: {
      width: '100%',
      height: '100%',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      objectFit: 'cover',
      borderRadius: '10px',
      border: 'solid var(--sib-color_surface-hover-background)',
      '&:hover': {
        '& svg': {
          color: 'var(--sib-color_primary-default)',
        },
        '& $thumbOverlay': {
          visibility: 'visible',
        },
      },
      '&.selected': {
        border: 'solid var(--sib-color_primary-default)',
        '& svg': {
          color: 'var(--sib-color_primary-default)',
        },
      },
    },
    thumbOverlay: {
      position: 'absolute',
      color: 'transparent',
      padding: '20px',
      fontSize: '14px',
      fontWeight: 'bold',
      width: '100%',
      height: '100%',
      borderRadius: '10px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '&:hover': {
        background: 'rgba(95, 93, 128, 0.6)',
        color: 'white',
      },
    },
  })
);

export default function BackgroundThumbnail({ thumbnail, imagePath, name, index }: BackgroundThumbnailProps) {
  const classes = useStyles();
  const { backgroundSettings, setBackgroundSettings } = useVideoContext();
  const isImage = thumbnail === 'image';
  const thumbnailSelected = isImage
    ? backgroundSettings.index === index && backgroundSettings.type === 'image'
    : backgroundSettings.type === thumbnail;
  const icons = {
    none: NoneIcon,
    blur: BlurIcon,
    image: null,
  };
  const ThumbnailIcon = icons[thumbnail];

  return (
    <div
      id={`video-calls-background-choice-${typeof index === 'number' ? index : thumbnail}`}
      className={classes.thumbContainer}
      onClick={() =>
        setBackgroundSettings({
          type: thumbnail,
          index: index,
        })
      }
      onKeyDown={(e) =>
        e.key === 'Enter' &&
        setBackgroundSettings({
          type: thumbnail,
          index: index,
        })
      }
      role="button"
      tabIndex={0}
    >
      {ThumbnailIcon ? (
        <div className={clsx(classes.thumbIconContainer, { selected: thumbnailSelected })}>
          <ThumbnailIcon className={classes.thumbIcon} />
        </div>
      ) : (
        <img className={clsx(classes.thumbImage, { selected: thumbnailSelected })} src={imagePath} alt={name} />
      )}
      <div className={classes.thumbOverlay}>{name}</div>
    </div>
  );
}
